import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  QrCode,
  Layers,
  Settings,
  Plus,
  Edit,
  Trash2,
  ArrowLeft,
  Loader2,
  Download,
  FileSpreadsheet,
  CheckCircle,
  BarChart3,
  Palette,
  Copy,
  ClipboardList
} from 'lucide-react';
import BadgeTemplateManager from '../../components/BadgeTemplateManager';

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyingInfo, setCopyingInfo] = useState(false);
  
  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ 
    category_name: '', 
    category_color: '#3B82F6', 
    display_order: 0,
    default_limits: {}
  });
  
  // Scanner limits dialog for a category
  const [scannerLimitsDialogOpen, setScannerLimitsDialogOpen] = useState(false);
  const [scannerLimitsCategory, setScannerLimitsCategory] = useState(null);
  const [scannerLimitsData, setScannerLimitsData] = useState([]);
  const [scannerLimitsEdits, setScannerLimitsEdits] = useState({});
  const [savingScannerLimits, setSavingScannerLimits] = useState(false);
  
  // Reports tab state
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportCategoryFilter, setReportCategoryFilter] = useState('all');
  const [reportScannerFilter, setReportScannerFilter] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [exportingReport, setExportingReport] = useState(false);
  
  const BOOTH_TYPES = [
    { key: 'entry', label: 'Entry', icon: '🚪' },
    { key: 'exit', label: 'Exit', icon: '🚶' },
    { key: 'meal', label: 'Meals', icon: '🍽️' },
    { key: 'drinks', label: 'Drinks', icon: '🥤' },
    { key: 'kit', label: 'Kit Pickup', icon: '📦' },
    { key: 'gift', label: 'Gift', icon: '🎁' },
    { key: 'tshirt', label: 'T-Shirt', icon: '👕' },
    { key: 'lounge', label: 'Lounge', icon: '✨' },
    { key: 'snacks', label: 'Snacks', icon: '🍿' },
    { key: 'photo', label: 'Photo', icon: '📸' },
  ];
  
  // Edit event
  const [editMode, setEditMode] = useState(false);
  const [editedEvent, setEditedEvent] = useState({});

  const fetchEventData = useCallback(async () => {
    try {
      const [eventRes, categoriesRes, dashboardsRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/categories`),
        api.get(`/events/${eventId}/dashboards`)
      ]);
      setEvent(eventRes.data);
      setCategories(categoriesRes.data || []);
      setDashboards(dashboardsRes.data || []);
      setEditedEvent(eventRes.data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  const handleCopyScannerInfo = async () => {
    setCopyingInfo(true);
    try {
      // Fetch credentials from the API
      const response = await api.get(`/events/${eventId}/dashboards/credentials`);
      const { event_name, campaign_id, dashboards: scannerCredentials } = response.data;
      
      // Build the formatted message
      const scannerLink = `${window.location.origin}/scanner/login`;
      
      let message = `📋 SCANNER LOGIN DETAILS\n`;
      message += `========================\n\n`;
      message += `Event Name: ${event_name}\n`;
      message += `Event ID: ${campaign_id}\n`;
      message += `Scanner Link: ${scannerLink}\n\n`;
      message += `------------------------\n`;
      message += `SCANNER CREDENTIALS\n`;
      message += `------------------------\n`;
      
      if (scannerCredentials && scannerCredentials.length > 0) {
        scannerCredentials.forEach((scanner) => {
          message += `\nBooth: ${scanner.dashboard_name}\n`;
          message += `Username: ${scanner.username}\n`;
          message += `Password: ${scanner.password}\n`;
        });
      } else {
        message += `\nNo scanners configured yet.\nGo to "Manage Scanners" to create scanner accounts.\n`;
      }
      
      await navigator.clipboard.writeText(message);
      toast.success('Scanner info copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy scanner info:', error);
      toast.error('Failed to copy scanner info');
    } finally {
      setCopyingInfo(false);
    }
  };

  const handleUpdateEvent = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}`, {
        event_name: editedEvent.event_name,
        event_location: editedEvent.event_location,
        event_description: editedEvent.event_description,
        expected_attendees: parseInt(editedEvent.expected_attendees),
        branding_color: editedEvent.branding_color,
        status: editedEvent.status
      });
      toast.success('Event updated successfully');
      setEditMode(false);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${eventId}/categories`, newCategory);
      toast.success('Category created');
      setCategoryDialogOpen(false);
      setNewCategory({ category_name: '', category_color: '#3B82F6', display_order: categories.length, default_limits: {} });
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    try {
      await api.put(`/events/${eventId}/categories/${editingCategory.category_id}`, {
        category_name: editingCategory.category_name,
        category_color: editingCategory.category_color,
        display_order: editingCategory.display_order,
        default_limits: editingCategory.default_limits || {}
      });
      toast.success('Category updated');
      setEditingCategory(null);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"?`)) return;
    try {
      await api.delete(`/events/${eventId}/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Open scanner limits dialog for a category
  const openScannerLimitsDialog = async (category) => {
    setScannerLimitsCategory(category);
    setScannerLimitsDialogOpen(true);
    
    // Build scanner limits data from existing dashboards
    const limitsData = dashboards.map(d => ({
      scanner_id: d.dashboard_id,
      scanner_name: d.dashboard_name,
      scanner_type: d.dashboard_type,
      limit: d.category_limits?.[category.category_id] ?? null,
      is_unlimited: d.category_limits?.[category.category_id] === null || d.category_limits?.[category.category_id] === undefined
    }));
    
    setScannerLimitsData(limitsData);
    
    // Initialize edits state
    const edits = {};
    limitsData.forEach(s => {
      edits[s.scanner_id] = s.limit;
    });
    setScannerLimitsEdits(edits);
  };

  // Save scanner limits for a category
  const handleSaveScannerLimits = async () => {
    if (!scannerLimitsCategory) return;
    
    setSavingScannerLimits(true);
    try {
      // Build the scanner_limits payload
      const scannerLimitsPayload = {};
      Object.entries(scannerLimitsEdits).forEach(([scannerId, limit]) => {
        scannerLimitsPayload[scannerId] = limit === '' || limit === null ? null : parseInt(limit);
      });
      
      await api.post(`/events/${eventId}/categories/${scannerLimitsCategory.category_id}/bulk-update-limits`, {
        scanner_limits: scannerLimitsPayload
      });
      
      toast.success('Scanner limits updated');
      setScannerLimitsDialogOpen(false);
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update scanner limits');
    } finally {
      setSavingScannerLimits(false);
    }
  };

  // Fetch scan summary report
  const fetchReportData = async () => {
    setLoadingReport(true);
    try {
      const params = {};
      if (reportCategoryFilter !== 'all') params.category_id = reportCategoryFilter;
      if (reportScannerFilter !== 'all') params.dashboard_id = reportScannerFilter;
      
      const response = await api.get(`/events/${eventId}/scan-summary-report`, { params });
      setReportData(response.data);
      
      // Expand all categories by default
      const expanded = {};
      response.data.categories?.forEach(cat => {
        expanded[cat.category_name] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoadingReport(false);
    }
  };

  // Export report to Excel
  const handleExportReport = async () => {
    setExportingReport(true);
    try {
      const params = {};
      if (reportCategoryFilter !== 'all') params.category_id = reportCategoryFilter;
      if (reportScannerFilter !== 'all') params.dashboard_id = reportScannerFilter;
      
      const response = await api.get(`/events/${eventId}/scan-summary-report/export`, {
        params,
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'scan_report.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExportingReport(false);
    }
  };

  // Format scan time for display
  const formatScanTime = (isoTime) => {
    try {
      const date = new Date(isoTime);
      return date.toLocaleString('en-US', { 
        day: '2-digit', 
        month: 'short', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return isoTime?.slice(0, 16) || '-';
    }
  };

  // Toggle category expand/collapse
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/events')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{event?.event_name}</h1>
          <p className="text-gray-500">{event?.event_location}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{event?.actual_attendees || 0}</p>
              <p className="text-sm text-gray-500">Attendees</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Layers className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{event?.total_categories || categories.length}</p>
              <p className="text-sm text-gray-500">Categories</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <QrCode className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{event?.total_dashboards || 0}</p>
              <p className="text-sm text-gray-500">Scanners</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{event?.total_scans || 0}</p>
              <p className="text-sm text-gray-500">Total Scans</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Link to={`/admin/events/${eventId}/attendees`}>
          <Button variant="outline">
            <Users className="h-4 w-4 mr-2" /> Manage Attendees
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/dashboards`}>
          <Button variant="outline">
            <QrCode className="h-4 w-4 mr-2" /> Manage Scanners
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/scan-logs`}>
          <Button variant="outline">
            <BarChart3 className="h-4 w-4 mr-2" /> View Scan Logs
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/analytics`}>
          <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600">
            <BarChart3 className="h-4 w-4 mr-2" /> Analytics Dashboard
          </Button>
        </Link>
      </div>

      {/* Scanner Portal Info Box */}
      {event?.campaign_id && (
        <Card className="mb-6 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <QrCode className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800">Scanner Portal</p>
                  <p className="text-sm text-emerald-600">Share scanner credentials with volunteers</p>
                </div>
              </div>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleCopyScannerInfo}
                disabled={copyingInfo}
                data-testid="copy-scanner-info"
              >
                {copyingInfo ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Copying...</>
                ) : (
                  <><ClipboardList className="h-4 w-4 mr-2" /> Copy Scanner Info</>
                )}
              </Button>
            </div>
            
            {/* Info Preview */}
            <div className="mt-4 p-3 bg-white/70 rounded-lg border border-emerald-200 space-y-2 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <span className="text-emerald-600 font-medium">Event Name:</span>
                  <p className="text-emerald-800">{event.event_name}</p>
                </div>
                <div>
                  <span className="text-emerald-600 font-medium">Event ID:</span>
                  <p className="font-mono text-emerald-800">{event.campaign_id}</p>
                </div>
                <div>
                  <span className="text-emerald-600 font-medium">Scanner Link:</span>
                  <p className="font-mono text-emerald-800 truncate text-xs">{window.location.origin}/scanner/login</p>
                </div>
              </div>
              <div className="pt-2 border-t border-emerald-100">
                <span className="text-emerald-600 font-medium">Scanners:</span>
                <span className="ml-2 text-emerald-800">
                  {dashboards.length > 0 
                    ? `${dashboards.length} scanner${dashboards.length > 1 ? 's' : ''} configured`
                    : 'No scanners yet - create them in "Manage Scanners"'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="badges">Badge Templates</TabsTrigger>
          <TabsTrigger value="reports" onClick={() => !reportData && fetchReportData()}>Reports</TabsTrigger>
          <TabsTrigger value="settings">Event Settings</TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Categories</CardTitle>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-category-button">
                    <Plus className="h-4 w-4 mr-2" /> Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Category</DialogTitle>
                    <DialogDescription>Create a new attendee category with default scan limits</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name</Label>
                      <Input
                        value={newCategory.category_name}
                        onChange={(e) => setNewCategory({...newCategory, category_name: e.target.value})}
                        placeholder="VIP, Speaker, Regular..."
                        required
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={newCategory.category_color}
                          onChange={(e) => setNewCategory({...newCategory, category_color: e.target.value})}
                          className="w-14 h-10 p-1"
                        />
                        <Input
                          value={newCategory.category_color}
                          onChange={(e) => setNewCategory({...newCategory, category_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2 border-t pt-4">
                      <Label className="text-base font-medium">Default Scan Limits</Label>
                      <p className="text-xs text-gray-500">Set default limits for this category. Leave empty for unlimited.</p>
                      <div className="grid grid-cols-2 gap-2">
                        {BOOTH_TYPES.map((booth) => (
                          <div key={booth.key} className="flex items-center gap-2">
                            <span className="text-sm w-20">{booth.icon} {booth.label}</span>
                            <Input
                              type="number"
                              min="0"
                              placeholder="∞"
                              className="w-16 h-8 text-sm"
                              value={newCategory.default_limits?.[booth.key] === 'unlimited' ? '' : (newCategory.default_limits?.[booth.key] || '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                setNewCategory({
                                  ...newCategory,
                                  default_limits: {
                                    ...newCategory.default_limits,
                                    [booth.key]: val === '' ? null : parseInt(val)
                                  }
                                });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" data-testid="submit-category-button">Create</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No categories yet. Create categories to organize your attendees.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {categories.map((category) => {
                    // Count how many scanners have limits for this category
                    const scannersWithLimits = dashboards.filter(
                      d => d.category_limits?.[category.category_id] !== null && 
                           d.category_limits?.[category.category_id] !== undefined
                    );
                    const scannerLimitCount = scannersWithLimits.length;
                    
                    return (
                      <div
                        key={category.category_id}
                        className="border rounded-lg overflow-hidden"
                        data-testid={`category-${category.category_id}`}
                      >
                        {/* Main row */}
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: category.category_color }}
                            />
                            <div>
                              <p className="font-medium">{category.category_name}</p>
                              <p className="text-sm text-gray-500">{category.attendee_count || 0} attendees</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {scannerLimitCount > 0 ? (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                                {scannerLimitCount} scanner limit{scannerLimitCount > 1 ? 's' : ''}
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                                No limits
                              </span>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openScannerLimitsDialog(category)}
                              title="Manage scanner limits for this category"
                              data-testid={`manage-limits-${category.category_id}`}
                            >
                              <Settings className="h-4 w-4 mr-1" />
                              Limits
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingCategory({...category, default_limits: category.default_limits || {}})}
                              title="Edit category"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteCategory(category.category_id, category.category_name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Scanner limits grid - show scanners with limits for this category */}
                        {scannerLimitCount > 0 && (
                          <div className="px-4 pb-4 pt-0">
                            <div className="bg-orange-50 rounded-lg p-3">
                              <p className="text-xs font-medium text-orange-700 mb-2">Scanner Scan Limits:</p>
                              <div className="flex flex-wrap gap-2">
                                {scannersWithLimits.map((scanner) => (
                                  <div 
                                    key={scanner.dashboard_id}
                                    className="flex items-center gap-1 bg-white border border-orange-200 rounded px-2 py-1 text-sm"
                                  >
                                    <span className="text-gray-600">{scanner.dashboard_name}:</span>
                                    <span className="font-semibold text-orange-600">
                                      {scanner.category_limits[category.category_id]} scans
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scanner Limits Dialog */}
          <Dialog open={scannerLimitsDialogOpen} onOpenChange={setScannerLimitsDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  Scanner Limits for &ldquo;{scannerLimitsCategory?.category_name}&rdquo;
                </DialogTitle>
                <DialogDescription>
                  Set how many times attendees in this category can be scanned at each scanner.
                </DialogDescription>
              </DialogHeader>
              
              {scannerLimitsCategory && (
                <div className="space-y-4">
                  {dashboards.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <QrCode className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No scanners created yet.</p>
                      <Link to={`/admin/events/${eventId}/dashboards`}>
                        <Button variant="link" className="mt-2">Create Scanners</Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {dashboards.map((scanner) => (
                          <div 
                            key={scanner.dashboard_id}
                            className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">{scanner.dashboard_name}</p>
                              <p className="text-xs text-gray-500">{scanner.dashboard_type}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                placeholder="∞"
                                className="w-20 h-9 text-center"
                                value={scannerLimitsEdits[scanner.dashboard_id] ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setScannerLimitsEdits({
                                    ...scannerLimitsEdits,
                                    [scanner.dashboard_id]: val === '' ? null : val
                                  });
                                }}
                              />
                              <span className="text-sm text-gray-500 w-12">scans</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded text-xs text-blue-700">
                        <p className="font-medium mb-1">💡 Tip:</p>
                        <p>Leave empty for unlimited scans. Limits are checked when scanning.</p>
                      </div>
                      
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setScannerLimitsDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveScannerLimits} disabled={savingScannerLimits}>
                          {savingScannerLimits ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            'Save Limits'
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Category Dialog */}
          <Dialog open={!!editingCategory} onOpenChange={() => setEditingCategory(null)}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Category</DialogTitle>
              </DialogHeader>
              {editingCategory && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category Name</Label>
                    <Input
                      value={editingCategory.category_name}
                      onChange={(e) => setEditingCategory({...editingCategory, category_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={editingCategory.category_color}
                        onChange={(e) => setEditingCategory({...editingCategory, category_color: e.target.value})}
                        className="w-14 h-10 p-1"
                      />
                      <Input
                        value={editingCategory.category_color}
                        onChange={(e) => setEditingCategory({...editingCategory, category_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="text-blue-700">
                      <span className="font-medium">💡 Scan Limits:</span> To set scan limits for this category, close this dialog and click the <strong>&quot;Limits&quot;</strong> button next to the category. This allows you to set limits per scanner (e.g., &quot;25th Lunch&quot;, &quot;Registration Kit&quot;).
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>
                    <Button onClick={handleUpdateCategory}>Save</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Badge Templates Tab */}
        <TabsContent value="badges">
          <div className="space-y-4">
            {/* Blank Badges Link */}
            <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-orange-900">On-Spot Registration</h3>
                  <p className="text-sm text-orange-700">Generate blank badges for walk-in registrations</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={() => navigate(`/admin/events/${eventId}/blank-badges`)}
                >
                  Manage Blank Badges
                </Button>
              </CardContent>
            </Card>
            
            <BadgeTemplateManager eventId={eventId} categories={categories} />
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Scan Summary Report</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Live view of all registrants with their scan history</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchReportData}
                  disabled={loadingReport}
                >
                  {loadingReport ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <><BarChart3 className="h-4 w-4 mr-1" /> Refresh</>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleExportReport}
                  disabled={exportingReport || !reportData}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {exportingReport ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting...</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" /> Download Excel</>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Category:</Label>
                  <Select value={reportCategoryFilter} onValueChange={(v) => { setReportCategoryFilter(v); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.category_color }} />
                            {cat.category_name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Scanner:</Label>
                  <Select value={reportScannerFilter} onValueChange={(v) => { setReportScannerFilter(v); }}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scanners</SelectItem>
                      {dashboards.map(d => (
                        <SelectItem key={d.dashboard_id} value={d.dashboard_id}>{d.dashboard_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={fetchReportData}>
                  Apply Filters
                </Button>
              </div>

              {/* Summary Stats */}
              {reportData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{reportData.summary?.total_registrants || 0}</p>
                    <p className="text-xs text-blue-600">Total Registrants</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{reportData.summary?.total_checked_in || 0}</p>
                    <p className="text-xs text-green-600">Checked In</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{reportData.summary?.total_scans || 0}</p>
                    <p className="text-xs text-purple-600">Total Scans</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-700">{reportData.summary?.check_in_percentage || 0}%</p>
                    <p className="text-xs text-orange-600">Check-in Rate</p>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loadingReport && (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              )}

              {/* No Data State */}
              {!loadingReport && !reportData && (
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Click &quot;Refresh&quot; to load the report data</p>
                </div>
              )}

              {/* Report Data - Grouped by Category */}
              {!loadingReport && reportData && reportData.categories?.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No attendees found for the selected filters</p>
                </div>
              )}

              {!loadingReport && reportData && reportData.categories?.map((category) => (
                <div key={category.category_name} className="mb-4 border rounded-lg overflow-hidden">
                  {/* Category Header - Clickable to expand/collapse */}
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                    style={{ backgroundColor: `${category.category_color}15` }}
                    onClick={() => toggleCategory(category.category_name)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: category.category_color }}
                      />
                      <span className="font-semibold">{category.category_name}</span>
                      <span className="text-sm text-gray-500">({category.attendees?.length || 0} registrants)</span>
                    </div>
                    <span className="text-gray-400">{expandedCategories[category.category_name] ? '▼' : '▶'}</span>
                  </div>

                  {/* Attendees Table - Collapsible */}
                  {expandedCategories[category.category_name] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Ticket ID</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Phone</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-600">Total</th>
                            {reportData.scanners?.map(scanner => (
                              <th key={scanner.dashboard_id} className="px-3 py-2 text-left font-medium text-gray-600 min-w-[140px]">
                                {scanner.dashboard_name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {category.attendees?.map((attendee, idx) => (
                            <tr key={attendee.attendee_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2 font-mono text-xs">{attendee.ticket_id}</td>
                              <td className="px-3 py-2 font-medium">{attendee.name}</td>
                              <td className="px-3 py-2 text-gray-600">{attendee.phone || '-'}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                  attendee.total_scans > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {attendee.total_scans}
                                </span>
                              </td>
                              {reportData.scanners?.map(scanner => {
                                const scanData = attendee.scanner_scans?.[scanner.dashboard_id];
                                const hasScans = scanData && scanData.scan_count > 0;
                                return (
                                  <td key={scanner.dashboard_id} className="px-3 py-2">
                                    {hasScans ? (
                                      <div className="flex items-start gap-1">
                                        <span className="text-green-600 font-bold">✓</span>
                                        <span className="text-xs text-gray-600">
                                          {scanData.scan_times?.map(t => formatScanTime(t)).join(', ')}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">✗</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Event Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Campaign ID Section */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-emerald-800 font-medium">Campaign ID (for Scanner Login)</Label>
                    <p className="text-sm text-emerald-600">Share this ID with your scanner operators</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-emerald-100 px-3 py-2 rounded text-lg font-mono font-bold text-emerald-800">
                      {editedEvent.campaign_id || 'Not set'}
                    </code>
                    {editMode && (
                      <Input
                        value={editedEvent.campaign_id || ''}
                        onChange={(e) => setEditedEvent({...editedEvent, campaign_id: e.target.value.toUpperCase()})}
                        placeholder="e.g., TECH2026"
                        className="w-32 uppercase"
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input
                    value={editedEvent.event_name || ''}
                    onChange={(e) => setEditedEvent({...editedEvent, event_name: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={editedEvent.event_location || ''}
                    onChange={(e) => setEditedEvent({...editedEvent, event_location: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expected Attendees</Label>
                  <Input
                    type="number"
                    value={editedEvent.expected_attendees || ''}
                    onChange={(e) => setEditedEvent({...editedEvent, expected_attendees: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Brand Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editedEvent.branding_color || '#3B82F6'}
                      onChange={(e) => setEditedEvent({...editedEvent, branding_color: e.target.value})}
                      className="w-14 h-10 p-1"
                      disabled={!editMode}
                    />
                    <Input
                      value={editedEvent.branding_color || '#3B82F6'}
                      onChange={(e) => setEditedEvent({...editedEvent, branding_color: e.target.value})}
                      className="flex-1"
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editedEvent.event_description || ''}
                  onChange={(e) => setEditedEvent({...editedEvent, event_description: e.target.value})}
                  disabled={!editMode}
                  rows={4}
                />
              </div>
              <div className="flex justify-end gap-2">
                {editMode ? (
                  <>
                    <Button variant="outline" onClick={() => { setEditMode(false); setEditedEvent(event); }}>
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateEvent} disabled={saving}>
                      {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setEditMode(true)}>
                    <Edit className="h-4 w-4 mr-2" /> Edit Event
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default EventDetail;
